/**
 * Sentry bootstrap for the rebar-iq-supercharged frontend. Imported on the very first line
 * of `main.tsx` so the SDK instruments fetch/XHR/history and the React error
 * pipeline before any application code runs.
 *
 * Production-only by design: a no-op in `vite dev` and any non-production build.
 * Sentry is enabled when this is a production build (`import.meta.env.PROD`) AND
 * a DSN is present — unless force-enabled/disabled via env. The DSN is a
 * publishable identifier, so the project default is hardcoded here and can be
 * overridden at build time with `VITE_SENTRY_DSN`.
 *
 * We capture as much useful debugging data as possible — errors, performance
 * traces and session replays — but NOT product analytics (out of scope for an
 * internal tool). Replay text is masked by default for privacy.
 */
import * as Sentry from '@sentry/react';
import { hasSignedLinkParams, scrubSignedLinks } from './utils/sentry_scrub';

const env = import.meta.env as unknown as {
  PROD?: boolean;
  MODE?: string;
  VITE_SENTRY_DSN?: string;
  VITE_SENTRY_ENVIRONMENT?: string;
  VITE_SENTRY_RELEASE?: string;
  VITE_SENTRY_TRACES_SAMPLE_RATE?: string;
  VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE?: string;
  VITE_SENTRY_REPLAYS_ERROR_SAMPLE_RATE?: string;
  VITE_SENTRY_FORCE_ENABLE?: string;
  VITE_SENTRY_DISABLE?: string;
};

/** Publishable DSN for the `rebar-iq-supercharged` Sentry project. */
const DEFAULT_DSN =
  'https://b3fb675e7b90dc32a26f248cf380eed7@o4506314504732672.ingest.us.sentry.io/4511513365315584';

const dsn = (env.VITE_SENTRY_DSN ?? DEFAULT_DSN).trim();

const forceEnable = env.VITE_SENTRY_FORCE_ENABLE === 'true';
const disabled = env.VITE_SENTRY_DISABLE === 'true';
const enabled = !disabled && dsn.length > 0 && (forceEnable || env.PROD === true);

/** Clamp a 0..1 sample-rate env var, falling back when missing/invalid. */
function rate(raw: string | undefined, fallback: number): number {
  if (raw == null || raw.trim() === '') return fallback;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 && n <= 1 ? n : fallback;
}

if (enabled) {
  Sentry.init({
    dsn,
    environment:
      env.VITE_SENTRY_ENVIRONMENT ?? (env.PROD ? 'production' : 'development'),
    release: env.VITE_SENTRY_RELEASE,

    // Structured Sentry Logs (Sentry.logger.*). Console breadcrumbs stay on by
    // default, so console output is attached to every event regardless.
    enableLogs: true,

    integrations: [
      // Automatic pageload/navigation transactions + fetch/XHR spans.
      Sentry.browserTracingIntegration(),
      // Session replay: full DOM/interaction timeline for debugging. Text is
      // masked and media blocked by default for privacy.
      // Not on signed-link pages (#222): replay stores `location.href` in a frame no hook can scrub.
      ...(hasSignedLinkParams(window.location.search)
        ? []
        : [
            Sentry.replayIntegration({
              maskAllText: true,
              blockAllMedia: true,
            }),
          ]),
    ],

    // Signed AV link (#222): `exp`/`sig` in URLs and the X-Av-Erfassung-* headers never leave the browser.
    beforeSend: (event) => scrubSignedLinks(event),
    beforeSendTransaction: (event) => scrubSignedLinks(event),
    beforeBreadcrumb: (breadcrumb) => scrubSignedLinks(breadcrumb),

    // Performance: trace every transaction by default. Lower via env to cut volume.
    tracesSampleRate: rate(env.VITE_SENTRY_TRACES_SAMPLE_RATE, 1.0),

    // Session replay sampling: 10% of ordinary sessions, 100% of sessions with
    // an error — the standard cost/coverage balance, overridable via env.
    replaysSessionSampleRate: rate(
      env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
      0.1,
    ),
    replaysOnErrorSampleRate: rate(
      env.VITE_SENTRY_REPLAYS_ERROR_SAMPLE_RATE,
      1.0,
    ),

    // Attach user IP / request context to aid debugging.
    sendDefaultPii: true,
  });
}
